/**
 * 기능 권한 Provider
 * 로그인 사용자의 기능 권한을 로드하여 Context로 제공합니다.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useGetIdentity } from '@refinedev/core';
import { PermissionContext } from '../hooks/usePermission';
import { PermissionApiClient } from '../services/permissionApi';

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  const { data: identity } = useGetIdentity<{
    id: string;
    name: string;
    email: string;
    fullName?: string;
  }>();

  const loadPermissions = useCallback(async (userId: number) => {
    try {
      const data = await PermissionApiClient.getUserFunctionPermissions(userId);
      setPermissions(data);
    } catch (error) {
      console.error('[PermissionProvider] 기능 권한 로드 실패:', error);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (identity?.id) {
      const userId = Number(identity.id);
      if (!isNaN(userId) && userId > 0) {
        loadPermissions(userId);
      } else {
        setLoading(false);
      }
    }
  }, [identity?.id, loadPermissions]);

  /**
   * 특정 permission_key에 대한 권한 확인
   * 부모 키가 false이면 자식도 false
   */
  const hasPermission = useCallback(
    (key: string): boolean => {
      // 권한 데이터가 아직 로드되지 않았으면 기본 허용
      if (loading) return true;

      // 정확한 키 매칭
      if (permissions[key] !== undefined) {
        return permissions[key];
      }

      // 부모 키가 false이면 자식도 false
      const parts = key.split('.');
      for (let i = 1; i < parts.length; i++) {
        const parentKey = parts.slice(0, i).join('.');
        if (permissions[parentKey] === false) {
          return false;
        }
      }

      // 기본: 권한 정보 없으면 허용
      return true;
    },
    [permissions, loading]
  );

  return (
    <PermissionContext.Provider value={{ permissions, loading, hasPermission }}>
      {children}
    </PermissionContext.Provider>
  );
};
